import { BrowserRouter, Navigate, Outlet, Route, Routes, useSearchParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import AppToaster from '../common/components/app-toaster/app-toaster';

import { AuthProvider, AuthRoute, ProtectedRoute, useAuth } from '../global/contexts/auth/auth';
import { ThemeProvider } from '../global/contexts/theme/theme';
import { queryClient } from '../core/network/query-client';
import Sidebar from '../features/navigation/sidebar';
import { LiveUpdatesProvider } from '../features/live/contexts/updates';
import { landingPath } from '../features/navigation/menu';
import Footer from '../features/footer/footer';

import LoginPage from '../pages/login';
import UnauthorizedPage from '../pages/unauthorized';
import ProfilePage from '../pages/admin/profile';
import ClientsPage from '../pages/admin/clients';
import UsersPage from '../pages/admin/users';
import TeamsPage from '../pages/admin/teams';
import ProjectsPage from '../pages/admin/projects';
import PlansPage from '../pages/admin/plans';
import PeoplePage from '../pages/admin/people';
import DevelopmentPage from '../pages/admin/development';
import RepositoriesPage from '../pages/admin/repositories';
import TiersPage from '../pages/admin/tiers';
import TasksPage from '../pages/admin/tasks';
import OperationPage from '../pages/admin/operation';
import TicketNewPage from '../pages/admin/ticket-new';
import LivePage from '../pages/admin/live';
import DocumentationPage from '../pages/admin/documentation';
import TokensPage from '../pages/admin/tokens';
import AgentsPage from '../pages/admin/agents';
import StandardsPage from '../pages/admin/standards';

// TSS vite/01 — app shell. Wires providers, router and layout chrome.
// TSS vite/03 §"Provider composition order" — Router → Auth → QueryClient.
//
// The seven sections of the rail are built: clients, users, teams, projects,
// repositories, tiers and tasks. Each one lists, creates and edits against the
// contract in mastermind_core/docs/api-reference.md.
//
// What is deliberately absent: claiming a task, reviewing a delivery, tier and
// task dependencies and directives. Those are not CRUD —
// they are the rules of the system — and they arrive with their tier.
//
// Every `domain` here is the permission the backend already enforces on that
// resource's list route (core/authz/roles.js), not a name invented on this
// side. Guarding with a code the API does not know would be a lock on a door
// with no wall.

export default function App() {
    return (
        <BrowserRouter>
            {/* Theme first: it writes an attribute on <html> and holds no data
                of its own, so nothing below it needs to wait for it. */}
            <ThemeProvider>
            <AuthProvider>
                <QueryClientProvider client={queryClient}>
                    <AppGate>
                        <AppRouter />
                    </AppGate>
                    {/* The only toaster: a second one freezes this one. */}
                    <AppToaster />
                </QueryClientProvider>
            </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

// Blocks rendering until the auth bootstrap resolves, so a guarded route never
// flashes unauthorized content during the initial /auth/me call.
function AppGate({ children }) {
    const { initializing } = useAuth();
    if (initializing) {
        return (
            <div className="boot-loader">
                <div className="boot-loader__spinner" />
                <span className="boot-loader__text">Verificando sesión...</span>
            </div>
        );
    }
    return children;
}

// The panel's chrome: navigation rail, content column, footer.
// TSS vite/01 §"Pages by audience" — pre-auth surfaces do NOT go through here:
// the login is full bleed and carries no rail. Mounting it inside the shell
// drew a light frame around it.
function ShellLayout() {
    // The live channel lives as long as the shell: one connection per session,
    // shared by every screen (features/live/contexts/updates).
    return (
        <LiveUpdatesProvider>
            <div className="app-shell">
                <Sidebar />
                <div className="app-shell__column">
                    <main className="app-shell__main">
                        <Outlet />
                    </main>
                    <Footer />
                </div>
            </div>
        </LiveUpdatesProvider>
    );
}

// The entry point, computed instead of fixed.
//
// `/` used to redirect to /admin/clients: not where anybody starts working, and
// a guaranteed trip to the unauthorized screen for any account without
// `clients:read`. `landingPath` answers with the first rail entry this account
// can actually read, falling back to the profile, which everybody has.
function Landing() {
    const { can } = useAuth();
    return <Navigate to={landingPath(can)} replace />;
}

// Una ruta que se mudó, CONSERVANDO LO QUE TRAÍA.
//
// `<Navigate to="/otra/ruta">` descarta la query, y eso convierte un enlace
// viejo con filtros en la pantalla correcta mostrando otra cosa — que se lee
// como un dato, no como un enlace roto, y por eso nadie lo reporta. Acá el
// destino se arma con los parámetros que venían, más los que la mudanza agrega.
function Moved({ to, add }) {
    const [params] = useSearchParams();
    const target = new URLSearchParams(params);
    for (const [key, value] of Object.entries(add ?? {})) target.set(key, value);
    const query = target.toString();
    return <Navigate to={`${to}${query ? `?${query}` : ''}`} replace />;
}

function AppRouter() {
    return (
        <Routes>
            {/* Full bleed, no shell. */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/logout" element={<Navigate to="/login?logout=true" replace />} />

            {/* Everything else lives inside the panel's chrome. */}
            <Route element={<ShellLayout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />

                {/* AuthRoute and not ProtectedRoute: your own account is not
                    behind a permission. Everybody who can log in has one. */}
                <Route path="/admin/profile" element={
                    <AuthRoute><ProfilePage /></AuthRoute>
                } />

                <Route path="/admin/clients" element={
                    <ProtectedRoute domain="clients:read"><ClientsPage /></ProtectedRoute>
                } />
                <Route path="/admin/standards" element={
                    <ProtectedRoute domain="standards:read"><StandardsPage /></ProtectedRoute>
                } />
                <Route path="/admin/standards/new" element={
                    <ProtectedRoute domain="standards:write"><ProtectedRoute domain="standards:read"><StandardsPage create /></ProtectedRoute></ProtectedRoute>
                } />
                <Route path="/admin/standards/:standardId" element={
                    <ProtectedRoute domain="standards:read"><StandardsPage /></ProtectedRoute>
                } />
                <Route path="/admin/standards/:standardId/versions/:versionId" element={
                    <ProtectedRoute domain="standards:read"><StandardsPage /></ProtectedRoute>
                } />
                {/* Creating and editing demand `clients:write`, which is the
                    permission the backend enforces on POST and PATCH. Guarding
                    both with `clients:read` would hand the form to somebody the
                    API answers 403 to. */}
                {/* `mode` travels as a prop and not as a URL param: this route
                    is static, so it declares no :id and useParams() comes back
                    empty. Without it the page fell through to the list and the
                    "+ Nuevo cliente" button looked dead — the URL changed and
                    the screen did not. */}
                <Route path="/admin/clients/new" element={
                    <ProtectedRoute domain="clients:write"><ClientsPage mode="create" /></ProtectedRoute>
                } />
                <Route path="/admin/clients/:id" element={
                    <ProtectedRoute domain="clients:write"><ClientsPage /></ProtectedRoute>
                } />
                {/* PERSONAS Y EQUIPOS, one section with two tabs.
                    `any` and not a single code: the two lists sit behind two
                    permissions, and holding either is enough to open the
                    section — the tab whose code is missing is not drawn. */}
                <Route path="/admin/people" element={
                    <ProtectedRoute any={['users:read', 'teams:read']}><PeoplePage /></ProtectedRoute>
                } />
                <Route path="/admin/people/:tab" element={
                    <ProtectedRoute any={['users:read', 'teams:read']}><PeoplePage /></ProtectedRoute>
                } />

                {/* The two list routes stay as redirects into their tab: links
                    written before the merge, and anything bookmarked, still
                    land where the list now lives. */}
                <Route path="/admin/users" element={<Navigate to="/admin/people/usuarios" replace />} />
                <Route path="/admin/users/new" element={
                    <ProtectedRoute domain="users:write"><UsersPage mode="create" /></ProtectedRoute>
                } />
                <Route path="/admin/users/:id" element={
                    <ProtectedRoute domain="users:write"><UsersPage /></ProtectedRoute>
                } />
                <Route path="/admin/teams" element={<Navigate to="/admin/people/equipos" replace />} />
                <Route path="/admin/teams/new" element={
                    <ProtectedRoute domain="teams:write"><TeamsPage mode="create" /></ProtectedRoute>
                } />
                <Route path="/admin/teams/:id" element={
                    <ProtectedRoute domain="teams:write"><TeamsPage /></ProtectedRoute>
                } />
                <Route path="/admin/projects" element={
                    <ProtectedRoute domain="projects:read"><ProjectsPage /></ProtectedRoute>
                } />
                <Route path="/admin/projects/new" element={
                    <ProtectedRoute domain="projects:write"><ProjectsPage mode="create" /></ProtectedRoute>
                } />
                {/* The project is a workspace, and reading it is `projects:read`.
                    Its tab lives in the path so a section can be linked to and
                    come back on the Back button. `edit` is a static segment, so
                    the router ranks it above `:tab`. */}
                <Route path="/admin/projects/:id" element={
                    <ProtectedRoute domain="projects:read"><ProjectsPage /></ProtectedRoute>
                } />
                <Route path="/admin/projects/:id/edit" element={
                    <ProtectedRoute domain="projects:write"><ProjectsPage mode="edit" /></ProtectedRoute>
                } />
                <Route path="/admin/projects/:id/:tab" element={
                    <ProtectedRoute domain="projects:read"><ProjectsPage /></ProtectedRoute>
                } />
                {/* Planning. Reading a plan is `plans:read`; writing a draft is
                    `plans:write`; APPROVING is `plans:approve`, a verb of its
                    own — signing where the tiers will come from is not the same
                    decision as editing the text. The approve button is gated
                    inside the screen, because approving also demands a human
                    session, the project's scope and being `lead` of one of its
                    teams, and none of those three is a permission code. */}
                <Route path="/admin/plans" element={
                    <ProtectedRoute domain="plans:read"><PlansPage /></ProtectedRoute>
                } />
                <Route path="/admin/plans/new" element={
                    <ProtectedRoute domain="plans:write"><PlansPage mode="create" /></ProtectedRoute>
                } />
                <Route path="/admin/plans/:id" element={
                    <ProtectedRoute domain="plans:read"><PlansPage /></ProtectedRoute>
                } />
                <Route path="/admin/plans/:id/v/:number" element={
                    <ProtectedRoute domain="plans:read"><PlansPage /></ProtectedRoute>
                } />

                {/* El listado se mudó a la pestaña de Proyectos; el alta y el
                    detalle se quedan donde estaban, porque son pantallas de un
                    repositorio y no un listado. */}
                <Route path="/admin/repositories" element={
                    <Moved to="/admin/projects" add={{ tab: 'repositorios' }} />
                } />
                <Route path="/admin/repositories/new" element={
                    <ProtectedRoute domain="repositories:write"><RepositoriesPage mode="create" /></ProtectedRoute>
                } />
                <Route path="/admin/repositories/:id" element={
                    <ProtectedRoute domain="repositories:write"><RepositoriesPage /></ProtectedRoute>
                } />
                <Route path="/admin/tiers" element={
                    <ProtectedRoute domain="tiers:read"><TiersPage /></ProtectedRoute>
                } />
                <Route path="/admin/tiers/new" element={
                    <ProtectedRoute domain="tiers:write"><TiersPage mode="create" /></ProtectedRoute>
                } />
                {/* READING IS `tiers:read`, EDITING IS `tiers:write`.
                    These used to be one route behind :write, which meant a
                    `technician` — who holds `breakdown:read` and reads tiers
                    all day — could not open one. The backend never conflated
                    the two; the panel did, because the only screen it had for
                    a tier was its form. */}
                <Route path="/admin/tiers/:id" element={
                    <ProtectedRoute domain="tiers:read"><TiersPage /></ProtectedRoute>
                } />
                <Route path="/admin/tiers/:id/edit" element={
                    <ProtectedRoute domain="tiers:write"><TiersPage mode="edit" /></ProtectedRoute>
                } />
                {/* `breakdown:read` and not `tiers:read`: it is its own code in
                    core/authz/roles.js, held by admin, technician and
                    agent_master, and it is the one the agreement and proposal
                    routes actually check. */}
                <Route path="/admin/tiers/:id/desglose" element={
                    <ProtectedRoute domain="breakdown:read"><TiersPage mode="breakdown" /></ProtectedRoute>
                } />
                {/* DESARROLLO: tiers, tareas, revisión y hallazgos en una
                    sección. La pestaña de tiers pide además `tiers:read`, y lo
                    comprueba la propia sección; `/admin/tiers` sigue siendo su
                    ruta suelta, con su propio guardia.

                    Tareas, revisión y hallazgos en una sección.
                    Las tres leen `GET /admin/tasks` con distinto filtro —
                    revisión pide `workflow_state=in_review`, hallazgos pide
                    `proposed` y separa las que tienen `origin_task_id`— y las
                    tres piden `tasks:read`: no hay código `findings:*` ni
                    `reviews:*` en core/authz/roles.js, y un hallazgo ES una
                    tarea. */}
                <Route path="/admin/development" element={
                    <ProtectedRoute domain="tasks:read"><DevelopmentPage /></ProtectedRoute>
                } />
                <Route path="/admin/development/:tab" element={
                    <ProtectedRoute domain="tasks:read"><DevelopmentPage /></ProtectedRoute>
                } />

                {/* Las rutas viejas siguen funcionando: lo enlazado antes del
                    cambio, y cualquier marcador, aterriza en su pestaña —
                    CON SUS FILTROS. Un `<Navigate to="...">` de destino fijo los
                    tira, y `/admin/tasks/list?state=proposed` habría aterrizado
                    sin el estado: la pantalla correcta mostrando otra cosa, que
                    es peor que un 404. */}
                <Route path="/admin/tasks" element={<Moved to="/admin/development/tareas" />} />
                <Route path="/admin/tasks/list" element={<Moved to="/admin/development/tareas" add={{ view: 'list' }} />} />
                {/* The way back: from a commit to the plan it came out of.
                    `tasks:read` is what the route asks for — whoever reaches
                    the task reads the plan it came from, the same rule as the
                    context package, and this one is read only. Declared before
                    `/:id` so the word `trace` is never read as an id. */}
                <Route path="/admin/tasks/trace" element={
                    <ProtectedRoute domain="tasks:read"><TasksPage mode="trace" /></ProtectedRoute>
                } />
                <Route path="/admin/tasks/new" element={
                    <ProtectedRoute domain="tasks:write"><TasksPage mode="create" /></ProtectedRoute>
                } />
                <Route path="/admin/tasks/:id" element={
                    <ProtectedRoute domain="tasks:read"><TasksPage /></ProtectedRoute>
                } />
                <Route path="/admin/tasks/:id/edit" element={
                    <ProtectedRoute domain="tasks:write"><TasksPage mode="edit" /></ProtectedRoute>
                } />

                {/* The credentials the agents act with. All three routes —
                    list, issue, revoke — ask for `tokens:admin`, because
                    issuing mints a credential and the list already tells you
                    who has an agent and when it last ran. */}
                <Route path="/admin/tokens" element={
                    <ProtectedRoute domain="tokens:admin"><TokensPage /></ProtectedRoute>
                } />
                <Route path="/admin/tokens/new" element={
                    <ProtectedRoute domain="tokens:admin"><TokensPage mode="create" /></ProtectedRoute>
                } />

                {/* Las dos se mudaron a pestañas de Desarrollo; las rutas
                    quedan como redirecciones que conservan el proyecto. */}
                <Route path="/admin/reviews" element={<Moved to="/admin/development/revision" />} />
                <Route path="/admin/findings" element={<Moved to="/admin/development/hallazgos" />} />

                {/* OPERACIÓN: lo que pasa después de que algo está desplegado.
                    Hoy, la ventana de tickets.

                    `tasks:read` para mirar y `tasks:write` para registrar, que
                    es lo que piden las cuatro rutas de tickets del backend: no
                    existe un código `tickets:*` en core/authz/roles.js.

                    El alta es ruta propia y no un modal: el cuerpo del reporte
                    es lo único de esta pantalla que después no se puede
                    reconstruir, y un diálogo que se cierra con Escape no es el
                    lugar para escribirlo. */}
                <Route path="/admin/operation" element={
                    <ProtectedRoute domain="tasks:read"><OperationPage /></ProtectedRoute>
                } />
                <Route path="/admin/operation/tickets/new" element={
                    <ProtectedRoute domain="tasks:write"><TicketNewPage /></ProtectedRoute>
                } />

                {/* The pulse. `events:read` is the permission the backend
                    enforces on GET /admin/events, which is where this screen
                    loads its state from before it starts listening. */}
                {/* Agent coordination uses the human session; each tab checks its own permission. The demo URL remains compatible. */}
                <Route path="/admin/agents/*" element={
                    <ProtectedRoute any={['events:read', 'machines:read']}>
                        <AgentsPage />
                    </ProtectedRoute>
                } />
                <Route path="/demo/coordinacion/*" element={
                    <ProtectedRoute any={['events:read', 'machines:read']}>
                        <AgentsPage />
                    </ProtectedRoute>
                } />

                <Route path="/admin/live" element={
                    <ProtectedRoute domain="events:read"><LivePage /></ProtectedRoute>
                } />
                {/* DOCUMENTACIÓN: a project's documents, one tab per kind.
                    `documents:read` is what the list and the versions ask for. */}
                <Route path="/admin/documentation" element={
                    <ProtectedRoute domain="documents:read"><DocumentationPage /></ProtectedRoute>
                } />
                <Route path="/admin/documentation/:tab" element={
                    <ProtectedRoute domain="documents:read"><DocumentationPage /></ProtectedRoute>
                } />

                <Route path="*" element={<Landing />} />
            </Route>
        </Routes>
    );
}
